/**
 * World setup data: the Classic World pairing, the start-bias graph, pars, crises, the World Assembly
 * agenda and default configuration.
 */
import type { AssemblyItemKind, CountryId, CrisisId, HeatStage, PairingMode, SlotId, VoteChoice, WorldConfig } from '../../shared/types.ts';

/** First-run pairing (fixed, hand-tuned). */
export const CLASSIC_WORLD: Record<CountryId, SlotId> = {
  kestran: 'harbor',
  meridia: 'highland',
  alvera: 'atoll',
  ironvale: 'heartland',
  karsova: 'sunbelt',
  aurem: 'canopy',
  brightwater: 'boreal',
  vellmark: 'delta',
};

/** The 4-regular start-bias graph (32 allowed pairings). Used by the pairing lints. */
export const ALLOWED_SLOTS: Record<CountryId, SlotId[]> = {
  ironvale: ['heartland', 'harbor', 'atoll', 'canopy'],
  brightwater: ['boreal', 'heartland', 'delta', 'highland'],
  meridia: ['harbor', 'boreal', 'delta', 'highland'],
  kestran: ['harbor', 'sunbelt', 'atoll', 'canopy'],
  vellmark: ['heartland', 'sunbelt', 'delta', 'highland'],
  alvera: ['heartland', 'sunbelt', 'atoll', 'canopy'],
  karsova: ['sunbelt', 'boreal', 'delta', 'highland'],
  aurem: ['harbor', 'boreal', 'atoll', 'canopy'],
};

/**
 * Par per Classic World pairing, a golf-style handicap (Index points = 50 + (final Index - par)). Pars come from
 * the simulator (scripts/simulate.ts --pars, 1,000 mixed bot games): the average par equals the average median
 * final National Index, and each nation's par is raised or lowered by how far its mean final points sit above
 * or below the world mean, so no pairing starts ahead on points. Kept within 45-55.
 */
export const CLASSIC_PARS: Record<CountryId, number> = {
  ironvale: 53.5,
  brightwater: 53,
  meridia: 52.5,
  kestran: 52.5,
  vellmark: 50.5,
  alvera: 50.5,
  karsova: 52,
  aurem: 52,
};

/**
 * Crossroads World: a lint-clean teacher alternative (docs/design-agendas.json). All four high-emitter slots go
 * to Western democracies, which breaks the mixed-bloc principle, so it is not the default. It uses its own pars.
 */
export const CROSSROADS_WORLD: Record<CountryId, SlotId> = {
  ironvale: 'harbor',
  brightwater: 'heartland',
  alvera: 'sunbelt',
  karsova: 'boreal',
  meridia: 'highland',
  vellmark: 'delta',
  kestran: 'canopy',
  aurem: 'atoll',
};

/** Crossroads handicap pars (scripts/simulate.ts --pairing crossroads --pars, clamped to 45-55). */
export const CROSSROADS_PARS: Record<CountryId, number> = {
  ironvale: 48.5,
  brightwater: 54,
  meridia: 53,
  kestran: 50.5,
  vellmark: 51.5,
  alvera: 54,
  karsova: 52.5,
  aurem: 51,
};

/** Named pairings (the World Agenda design keeps the Classic World as the default). */
export const PAIRINGS: Record<PairingMode, Record<CountryId, SlotId>> = {
  classic: CLASSIC_WORLD,
  crossroads: CROSSROADS_WORLD,
};

export const PARS_BY_PAIRING: Record<PairingMode, Record<CountryId, number>> = {
  classic: CLASSIC_PARS,
  crossroads: CROSSROADS_PARS,
};

export const STAGE_NAMES: Record<HeatStage, string> = {
  1: 'Warming',
  2: 'Disruption',
  3: 'Danger',
  4: 'Breakdown',
};

export const DEFAULT_CONFIG: WorldConfig = {
  nNations: 8,
  pairing: 'classic',
  maxTurns: 20,
  assemblyTurns: [5, 9, 13, 17],
  /** Reserved, no effect (strategic weapons are always off). */
  strategicWeapons: false,
  autoSignAtDeadline: true,
  botSkill: 'strong',
  climatePolicy: 'doctrine',
};

export const DEFAULT_ORDERS_MS = 18 * 60 * 1000;
export const DEFAULT_BRIEFING_MS = 0;

export interface CrisisData {
  id: CrisisId;
  turn: number;
  title: string;
  /** Student-facing text that shows responders and dignity. */
  body: string;
  response: string;
  image: string | null;
  icon: string;
  /** Climate crises count as climate hits for Loss and Damage payouts. */
  climate: boolean;
}

export const CRISES: Record<CrisisId, CrisisData> = {
  pirates: {
    id: 'pirates', turn: 3, title: 'Pirates on the shipping lanes',
    body: 'Armed boats are stopping cargo ships near the Atoll and Harbor lanes. Coast guards and ship crews are working together to keep sailors safe.',
    response: 'Fund a naval patrol (1 Treasury). A Navy of 2 or more also protects your ships.',
    image: 'crisis-02-pirates', icon: 'anchor', climate: false,
  },
  drought: {
    id: 'drought', turn: 6, title: 'A world drought',
    body: 'Rain has failed across many regions. Farmers, aid workers and water engineers are sharing what they know to save the harvest.',
    response: 'Fund drought relief (2 Treasury): the Food hit is 1 smaller and the Well-being hit is halved.',
    image: 'crisis-03-drought', icon: 'sun', climate: true,
  },
  pandemic: {
    id: 'pandemic', turn: 9, title: 'A new illness spreads',
    body: 'A fast-spreading illness has reached several countries. Nurses, doctors and scientists are working around the clock, and neighbors are checking on the sick.',
    response: 'Fund a health response (2 Treasury): the hit is halved. If most nations respond, the world contains the illness.',
    image: 'crisis-04-pandemic', icon: 'cross', climate: false,
  },
  island: {
    id: 'island', turn: 12, title: 'Energy found under a lonely island',
    body: 'Surveyors have found oil and gas under an unowned island in open sea. No nation owns it. Whoever develops it gains Energy, and the world gains Carbon.',
    response: 'Develop the island (2 Treasury, Navy 1 or more): +3 Energy every turn and Carbon +2. If two nations try in the same turn, neither gets it.',
    image: 'crisis-05-energy-discovery', icon: 'island', climate: false,
  },
  refugees: {
    id: 'refugees', turn: 14, title: 'Families forced from their homes',
    body: 'Floods, rising seas and drought have forced families to leave their homes in the Atoll, Delta and dry lands. Aid groups and host towns are ready to help them start again.',
    response: 'Welcome refugee groups. Each costs 1 Food and raises Social Cohesion. Or pay 2 Treasury per group to help them settle elsewhere.',
    image: 'crisis-06-refugees', icon: 'people', climate: true,
  },
  flooding: {
    id: 'flooding', turn: 15, title: 'Coastal and river flooding',
    body: 'Storms push the sea and rivers over their banks. Rescue crews, neighbors and volunteers are moving families to safety.',
    response: 'Fund flood relief (2 Treasury): the damage is halved. Resilience and a Mangrove Belt make floods smaller.',
    image: 'crisis-01-coastal-flooding', icon: 'wave', climate: true,
  },
  terror: {
    id: 'terror', turn: 18, title: 'An attack in a world city',
    body: 'An attack in a busy world city has shaken every nation. First responders acted fast, and people lined up to give blood.',
    response: 'Pay for a joint security response (1 Treasury). Counterintelligence this turn also keeps you safe. If 4 or more nations help, every nation gets Security back.',
    image: null, icon: 'shield', climate: false,
  },
};

export interface AgendaItemData {
  kind: AssemblyItemKind;
  title: string;
  text: string;
  climate: boolean;
  choices: VoteChoice[];
}

/** Standing and scripted World Assembly items by session turn. */
export const ASSEMBLY_AGENDA: Record<number, AgendaItemData[]> = {
  5: [
    {
      kind: 'create_fund', title: 'Create the Loss and Damage Fund', climate: true, choices: ['yes', 'no', 'abstain'],
      text: 'Create a world fund that pays nations hit by climate disasters. Nations pledge money at each Assembly. At least half of each payout goes to the most vulnerable nations.',
    },
  ],
  9: [
    {
      kind: 'health_compact', title: 'World Health Compact', climate: false, choices: ['yes', 'no', 'abstain'],
      text: 'Every nation shares medical supplies and data. If it passes, each nation pays 1 Treasury and every pandemic hit is 1 smaller.',
    },
    {
      kind: 'payout_formula', title: 'How the fund pays out', climate: true, choices: ['equal', 'damage', 'vulnerability'],
      text: 'Choose how the Loss and Damage Fund splits its money: equal shares, by damage suffered, or most vulnerable first.',
    },
  ],
  13: [
    {
      kind: 'island_commons', title: 'Island Commons Agreement', climate: true, choices: ['yes', 'no', 'abstain'],
      text: 'Protect the new island as a shared commons. If it passes before anyone develops it, no nation may drill there and every nation gains +2 Environment.',
    },
  ],
  17: [
    {
      kind: 'clean_tech_push', title: 'Clean Technology Push', climate: true, choices: ['yes', 'no', 'abstain'],
      text: 'Nations share clean-tech patents. If it passes, Green Transition costs 1 less for everyone for the rest of the game.',
    },
  ],
};

/** Each nation may add at most this many motions per session. */
export const MOTIONS_PER_SESSION = 1;
